// world.js
import * as THREE from 'three';
import { WorldChunk } from './worldChunk';
import { DataStore } from './dataStore';
import { blocks } from './blocks';
import { RNG } from './rng';
import { getChunkOwner } from '@/config/world';
import { doc, setDoc, getDoc, collection, onSnapshot } from 'firebase/firestore';
import { functions, db } from "@/config/firebase"
import { httpsCallable } from 'firebase/functions';
import { setupModificationHandler } from './worldModification';

export class World extends THREE.Group {
  asyncLoading = true;
  // drawDistance = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? 1 : 2;
  // chunkSize = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? { width: 16, height: 16 } : { width: 32, height: 32 };
  drawDistance = 2;
  chunkSize = { width: 32, height: 32 };

  modCache = {};

  statusTimeoutId = null;

  params = {
    seed: 0,
    terrain: { scale: 100, magnitude: 8, offset: 6, waterOffset: 4 },
    biomes: {
      scale: 500,
      variation: { amplitude: 0.2, scale: 50 },
      tundraToTemperate: 0.25, temperateToJungle: 0.5, jungleToDesert: 0.75
    },
    trees: { trunk: { minHeight: 4, maxHeight: 7 }, canopy: { minRadius: 3, maxRadius: 3, density: 0.7 }, frequency: 0.01 },
    clouds: { scale: 30, density: 0.3 }
  };

  dataStore = new DataStore();
  addBlockToWorldCallable = httpsCallable(functions, 'addBlockToWorld');
  destroyBlockCallable = httpsCallable(functions, 'destroyBlock');

  constructor(seed = 0) {
    super();
    this.seed = seed;
    document.addEventListener('keydown', (ev) => {
      if (ev.code === 'F1') this.save();
      if (ev.code === 'F2') this.load();
    });
    this.eventListeners = new Map();
    this.startRealtimeModificationSync();
    setupModificationHandler(this);
  }

  save() {
    localStorage.setItem('minecraft_params', JSON.stringify(this.params));
    localStorage.setItem('minecraft_data', JSON.stringify(this.dataStore.data));
    this._notify('GAME SAVED');
  }

  load() {
    const params = localStorage.getItem('minecraft_params');
    const data = localStorage.getItem('minecraft_data');

    if (params) this.params = JSON.parse(params);
    if (data) this.dataStore.data = JSON.parse(data);

    this._notify('GAME LOADED');
    this.generate();
  }

  _notify(text, type = 'info') {
    this.emit('notify', { text, type });

    const status = document.getElementById('status');
    if (status) {
      if (this.statusTimeoutId) {
        clearTimeout(this.statusTimeoutId);
      }
      status.innerText = text;
      status.classList.add('show');
      this.statusTimeoutId = setTimeout(() => {
        status.classList.remove('show');
        setTimeout(() => {
          status.innerText = '';
          this.statusTimeoutId = null;
        }, 400);
      }, 3000);
    }
  }

  generate(clearCache = true) {
    if (clearCache) this.dataStore.clear();
    this.disposeChunks();
    for (let x = -this.drawDistance; x <= this.drawDistance; x++) {
      for (let z = -this.drawDistance; z <= this.drawDistance; z++) {
        this.generateChunk(x, z);
      }
    }

  }

  update(player) {
    const visibleChunks = this.getVisibleChunks(player);
    const chunksToAdd = this.getChunksToAdd(visibleChunks);
    this.removeUnusedChunks(visibleChunks);
    for (const chunk of chunksToAdd) {
      this.generateUpdateChunk(chunk.x, chunk.z);
    }
  }

  worldToChunkCoords(x, y, z) {
    const chunkCoords = { x: Math.floor(x / this.chunkSize.width), z: Math.floor(z / this.chunkSize.width) };
    const blockCoords = { x: x - this.chunkSize.width * chunkCoords.x, y, z: z - this.chunkSize.width * chunkCoords.z };
    return { chunk: chunkCoords, block: blockCoords };
  }

  getVisibleChunks(player) {
    const chunks = [];
    const coords = this.worldToChunkCoords(player.position.x, player.position.y, player.position.z).chunk;
    for (let x = coords.x - this.drawDistance; x <= coords.x + this.drawDistance; x++) {
      for (let z = coords.z - this.drawDistance; z <= coords.z + this.drawDistance; z++) {
        chunks.push({ x, z });
      }
    }
    return chunks;
  }

  getChunksToAdd(visibleChunks) {
    return visibleChunks.filter(chunk => !this.children.some(obj => obj.userData.x === chunk.x && obj.userData.z === chunk.z));
  }

  removeUnusedChunks(visibleChunks) {
    const toRemove = this.children.filter(chunk => !visibleChunks.some(c => c.x === chunk.userData.x && c.z === chunk.userData.z));
    for (const chunk of toRemove) {
      chunk.disposeInstances?.();
      this.remove(chunk);
    }
  }

  generateChunk(x, z) {
    const chunk = new WorldChunk(this.chunkSize, this.params, this.dataStore);
    chunk.position.set(x * this.chunkSize.width, 0, z * this.chunkSize.width);
    chunk.userData = { x, z };
    chunk.generate();
    // if (this.asyncLoading) {
    //   requestIdleCallback(chunk.generate.bind(chunk), { timeout: 1000 });
    // } else {
    // }
    this.add(chunk);

    this.emit('chunkCreated', {
      chunkKey: `${x}_${z}`,
      chunk: chunk
    });
  }

  generateUpdateChunk(x, z) {
    const chunk = new WorldChunk(this.chunkSize, this.params, this.dataStore);
    chunk.position.set(x * this.chunkSize.width, 0, z * this.chunkSize.width);
    chunk.userData = { x, z };

    chunk.onLoaded = () => {
      this.emit('chunkCreated', {
        chunkKey: `${x}_${z}`,
        chunk: chunk
      });
    };

    if (this.asyncLoading) {
      requestIdleCallback(chunk.generateDuringUpdate.bind(chunk), { timeout: 1000 });
    } else {
      chunk.generateDuringUpdate();
    }
    this.add(chunk);
  }

  disposeChunks() {
    this.traverse(chunk => chunk.disposeInstances?.());
    this.clear();
  }

  getChunk(chunkX, chunkZ) {
    return this.children.find(chunk => chunk.userData.x === chunkX && chunk.userData.z === chunkZ);
  }

  getBlock(x, y, z) {
    const coords = this.worldToChunkCoords(x, y, z);
    const chunkKey = `${coords.chunk.x}_${coords.chunk.z}`;
    const coordKey = `${coords.block.x}_${coords.block.y}_${coords.block.z}`;

    const cached = this.modCache[chunkKey]?.[coordKey];
    if (cached !== undefined) {
      return { id: cached };
    }

    const chunk = this.getChunk(coords.chunk.x, coords.chunk.z);
    return chunk?.loaded ? chunk.getBlock(coords.block.x, coords.block.y, coords.block.z) : null;
  }

  async canModifyBlock(x, z, userId) {
    const coords = this.worldToChunkCoords(x, 0, z).chunk;
    const owner = await getChunkOwner(coords.x, coords.z);
    return owner === userId;
  }

  async addBlock(x, y, z, blockId, userId) {
    try {
      const coords = this.worldToChunkCoords(x, y, z);
      const chunk = this.getChunk(coords.chunk.x, coords.chunk.z);
      if (!chunk) return;

      const result = await this.addBlockToWorldCallable({
        blockId,
        locationType: "world",
        chunk: {
          x: coords.chunk.x,
          z: coords.chunk.z
        },
        x: coords.block.x,
        y: coords.block.y,
        z: coords.block.z,
      });

      const responseData = result.data;
      if (!responseData.success) {
        // This case should theoretically not be reached if the function throws HttpsErrors
        console.error("addBlockToWorldCallable returned success: false unexpectedly.");
        this._notify("BlockType not in inventory!")
      }

    } catch (error) {
      console.error(`Failed to call addBlockToWorldCallable or inventory check failed:`, error);
      const message = error.message || "Adding block failed!";
      this._notify(message, 'error');
    }
  };

  async removeBlock(x, y, z, userId) {
    try {
      const coords = this.worldToChunkCoords(x, y, z);
      const chunk = this.getChunk(coords.chunk.x, coords.chunk.z);
      if (!chunk) return;
      // Await the result of the callable function
      const result = await this.destroyBlockCallable({
        locationType: "world",
        chunk: {
          x: coords.chunk.x,
          z: coords.chunk.z
        },
        x: coords.block.x,
        y: coords.block.y,
        z: coords.block.z,
      });
      const responseData = result.data;

      if (!responseData.success) {
        // Handle specific server-side failure messages
        const errorMessage = responseData?.message || "Cannot remove block.";
        this._notify(errorMessage);
        console.error("Server reported failure:", responseData);
      }
    } catch (error) {
      // Catch errors thrown by the callable function itself (network, server error, HttpsError)
      console.error("Error calling destroyBlockCallable:", error);
      // Provide user feedback for the error
      this._notify(error.message || "Failed to remove block.", 'error');
    }
  }

  _refreshSurroundings(x, y, z) {
    this.revealBlock(x - 1, y, z);
    this.revealBlock(x + 1, y, z);
    this.revealBlock(x, y - 1, z);
    this.revealBlock(x, y + 1, z);
    this.revealBlock(x, y, z - 1);
    this.revealBlock(x, y, z + 1);
  }

  revealBlock(x, y, z) {
    const coords = this.worldToChunkCoords(x, y, z);
    const chunk = this.getChunk(coords.chunk.x, coords.chunk.z);
    if (!chunk || !chunk.loaded) return;
    if (!chunk.isBlockObscured(coords.block.x, coords.block.y, coords.block.z)) {
      chunk.addBlockInstance(coords.block.x, coords.block.y, coords.block.z);
    }
  }

  hideBlock(x, y, z) {
    const coords = this.worldToChunkCoords(x, y, z);
    const chunk = this.getChunk(coords.chunk.x, coords.chunk.z);
    if (!chunk || !chunk.loaded) return;
    if (chunk.isBlockObscured(coords.block.x, coords.block.y, coords.block.z)) {
      chunk.deleteBlockInstance(coords.block.x, coords.block.y, coords.block.z);
    }
  }

  startRealtimeModificationSync() {
    const ref = collection(db, 'chunkMods');
    this.unsubscribeSnapshot = onSnapshot(ref, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const chunkKey = change.doc.id;
        const data = change.doc.data();
        this.modCache[chunkKey] = data.blocks || {};

        const [chunkX, chunkZ] = chunkKey.split('_').map(Number);
        const chunk = this.getChunk(chunkX, chunkZ);

        if (chunk?.loaded) {
          for (const coord in data.blocks) {
            const [x, y, z] = coord.split('_').map(Number);
            const blockId = data.blocks[coord];
            const currentBlock = chunk.getBlock(x, y, z);

            // Skip if no actual change
            if (currentBlock.id === blockId) continue;

            if (blockId === blocks.empty.id) {
              chunk.removeBlock(x, y, z); // Handles mesh updates internally
            } else {
              chunk.addBlock(x, y, z, blockId); // Handles mesh updates internally
            }

            this._refreshSurroundings(x, y, z);
          }
        }
      });
    });
  }

  dispose() {
    if (this.unsubscribeSnapshot) {
      this.unsubscribeSnapshot();
    }
    this.traverse(obj => {
      if (obj !== this) obj.dispose?.();
    });
    this.clear();
  }

  emit(eventName, data) {
    const listeners = this.eventListeners.get(eventName);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  on(eventName, callback) {
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, []);
    }
    this.eventListeners.get(eventName).push(callback);
  }
}